import { useMemo } from "react";

const JS_WORDS = [
  "async", "await", "break", "case", "catch", "class", "const", "continue",
  "default", "delete", "do", "else", "export", "extends", "false", "finally",
  "for", "from", "function", "if", "import", "in", "instanceof", "let", "new",
  "null", "of", "return", "static", "super", "switch", "this", "throw", "true",
  "try", "typeof", "undefined", "var", "void", "while", "yield"
];

const PY_WORDS = [
  "and", "as", "assert", "async", "await", "break", "class", "continue",
  "def", "del", "elif", "else", "except", "False", "finally", "for", "from",
  "global", "if", "import", "in", "is", "lambda", "None", "nonlocal", "not",
  "or", "pass", "raise", "return", "True", "try", "while", "with", "yield"
];

const SH_WORDS = [
  "case", "do", "done", "elif", "else", "esac", "fi", "for", "function",
  "if", "in", "select", "then", "until", "while"
];

const TOKEN_STYLE = {
  attr: { color: "#0ea5e9" },
  boolean: { color: "#dc2626", fontWeight: 700 },
  color: { color: "#f97316", fontWeight: 700 },
  comment: { color: "#64748b", fontStyle: "italic" },
  function: { color: "#a855f7" },
  keyword: { color: "#7c3aed", fontWeight: 700 },
  number: { color: "#d97706" },
  property: { color: "#2563eb" },
  punctuation: { color: "#94a3b8" },
  string: { color: "#15803d" },
  tag: { color: "#dc2626", fontWeight: 700 },
};

function keywordRegex(words) {
  return new RegExp(`\\b(?:${words.join("|")})\\b`, "g");
}

function languageFromClass(className, fallback = "code") {
  const match = /^language-(.+)$/.exec(className || "");
  return (match?.[1] || fallback || "code").toLowerCase();
}

function normalizeLang(lang) {
  const value = (lang || "code").toLowerCase();
  if (["js", "jsx", "javascript", "mjs", "cjs"].includes(value)) return "javascript";
  if (["ts", "tsx", "typescript"].includes(value)) return "typescript";
  if (["html", "htm", "xml", "svg"].includes(value)) return "html";
  if (["sh", "bash", "zsh", "shell"].includes(value)) return "shell";
  if (["py", "python"].includes(value)) return "python";
  return value;
}

function patternsFor(lang) {
  const normalized = normalizeLang(lang);
  if (normalized === "html") {
    return [
      ["comment", /<!--.*?-->/g],
      ["string", /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g],
      ["tag", /<\/?[\w:-]+/g],
      ["attr", /\b[\w:-]+(?=\=)/g],
      ["punctuation", /[<>/=]/g],
    ];
  }
  if (normalized === "css") {
    return [
      ["comment", /\/\*.*?\*\//g],
      ["string", /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g],
      ["color", /#[0-9a-fA-F]{3,8}\b/g],
      ["keyword", /@[a-z-]+/gi],
      ["property", /\b[-a-zA-Z]+(?=\s*:)/g],
      ["number", /\b\d+(?:\.\d+)?(?:px|rem|em|vh|vw|%|s|ms)?\b/g],
      ["punctuation", /[{}()[\]:;,]/g],
    ];
  }
  if (normalized === "json") {
    return [
      ["property", /"(?:\\.|[^"\\])*"(?=\s*:)/g],
      ["string", /"(?:\\.|[^"\\])*"/g],
      ["boolean", /\b(?:true|false|null)\b/g],
      ["number", /-?\b\d+(?:\.\d+)?(?:e[+-]?\d+)?\b/gi],
      ["punctuation", /[{}()[\]:,]/g],
    ];
  }
  if (normalized === "python") {
    return [
      ["comment", /#.*$/g],
      ["string", /"""[\s\S]*?"""|'''[\s\S]*?'''|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g],
      ["keyword", keywordRegex(PY_WORDS)],
      ["number", /\b\d+(?:\.\d+)?\b/g],
      ["function", /\b[A-Za-z_]\w*(?=\s*\()/g],
      ["punctuation", /[{}()[\]:;,.]/g],
    ];
  }
  if (normalized === "shell") {
    return [
      ["comment", /#.*$/g],
      ["string", /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g],
      ["keyword", keywordRegex(SH_WORDS)],
      ["number", /\b\d+(?:\.\d+)?\b/g],
      ["function", /\b[A-Za-z_][\w-]*(?=\s)/g],
      ["punctuation", /[{}()[\];|&]/g],
    ];
  }
  if (["javascript", "typescript"].includes(normalized)) {
    return [
      ["comment", /\/\/.*$|\/\*.*?\*\//g],
      ["string", /`(?:\\.|[^`\\])*`|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g],
      ["keyword", keywordRegex(JS_WORDS)],
      ["number", /\b\d+(?:\.\d+)?\b/g],
      ["function", /\b[A-Za-z_$][\w$]*(?=\s*\()/g],
      ["punctuation", /[{}()[\];,.]/g],
    ];
  }
  return [
    ["comment", /\/\/.*$|#.*$/g],
    ["string", /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g],
    ["number", /\b\d+(?:\.\d+)?\b/g],
    ["punctuation", /[{}()[\];,.]/g],
  ];
}

function tokenizeLine(line, lang) {
  const matches = [];
  patternsFor(lang).forEach(([type, pattern], priority) => {
    for (const match of line.matchAll(pattern)) {
      const text = match[0];
      if (!text) continue;
      const start = match.index ?? 0;
      matches.push({ type, text, start, end: start + text.length, priority });
    }
  });

  matches.sort((a, b) => a.start - b.start || b.end - a.end || a.priority - b.priority);

  const pieces = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start < cursor) continue;
    if (match.start > cursor) {
      pieces.push({ type: "plain", text: line.slice(cursor, match.start) });
    }
    pieces.push({ type: match.type, text: match.text });
    cursor = match.end;
  }
  if (cursor < line.length) pieces.push({ type: "plain", text: line.slice(cursor) });
  return pieces;
}

function renderLine(line, lang) {
  if (!line) return " ";
  return tokenizeLine(line, lang).map((piece, index) => {
    if (piece.type === "plain") return piece.text;
    return (
      <span className={`cf-code-token cf-code-token-${piece.type}`} style={TOKEN_STYLE[piece.type]} key={`${piece.type}-${index}`}>
        {piece.text}
      </span>
    );
  });
}

function CodeBlockView({ content, className, lang, theme }) {
  const value = String(content ?? "");
  const language = normalizeLang(lang || languageFromClass(className));
  const lines = useMemo(() => value.split("\n"), [value]);
  const t = theme || {};
  const border = t.border || t.border2 || t.borderSubtle || "rgba(127,127,127,0.22)";
  const gutterBg = t.surfaceAlt || t.inputBg || "rgba(127,127,127,0.06)";
  const lineNoColor = t.textMuted || t.text3 || "#94a3b8";
  const textColor = t.text || "#0f172a";

  return (
    <pre className="cf-code-pre" style={{
      overflow: "auto",
      margin: 0,
      padding: 0,
      fontSize: 12,
      color: textColor,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace",
      lineHeight: 1.65,
      tabSize: 2,
    }}>
      <code className={className} style={{ display: "table", minWidth: "100%", borderCollapse: "collapse" }}>
        {lines.map((line, index) => {
          const first = index === 0;
          const last = index === lines.length - 1;
          const top = first ? 10 : 0;
          const bottom = last ? 10 : 0;
          return (
            <span className="cf-code-line" key={index} style={{ display: "table-row" }}>
              <span className="cf-code-line-number" aria-hidden="true" style={{
                display: "table-cell",
                position: "sticky",
                left: 0,
                zIndex: 1,
                minWidth: 36,
                padding: `${top}px 10px ${bottom}px 12px`,
                borderRight: `1px solid ${border}`,
                background: gutterBg,
                color: lineNoColor,
                textAlign: "right",
                userSelect: "none",
              }}>
                {index + 1}
              </span>
              <span className="cf-code-source" style={{
                display: "table-cell",
                padding: `${top}px 12px ${bottom}px 12px`,
                whiteSpace: "pre",
              }}>
                {renderLine(line, language)}
              </span>
            </span>
          );
        })}
      </code>
    </pre>
  );
}

export { CodeBlockView, languageFromClass, normalizeLang };
export default CodeBlockView;
